/**
 * Get the selected multi-environment IDs from Redux state
 *
 * Default behavior (first time, no selection saved):
 * - If multi-environment is active: returns ALL environment IDs ("Todos los Entornos")
 * - If multi-environment is NOT active: returns user's default environment
 *
 * Once user changes selection, it persists between sessions via Redux
 *
 * @param {Object} state - Redux state
 * @returns {Array<number>} Array of selected environment IDs
 */
export const getSelectedMultiEnvironmentIds = (state) => {
    const savedSelection = state.config?.components?.selectedMultiEnvironments;

    // If there's a saved selection, use it (user has already interacted with the selector)
    if (savedSelection && savedSelection.length > 0) {
        return savedSelection;
    }

    // First time or no saved selection
    const environments = state?.serverList?.tblSucursales?.data || [];
    const multiEnvironmentPermission = state.config?.permission?.multiEnvironment;
    const multiEnvironmentActive = multiEnvironmentPermission && environments.length > 1;
    const defaultEnvironment = state.config?.userData?.idEntorno;

    if (multiEnvironmentActive) {
        // Multi-environment active: default to "Todos los Entornos"
        return environments.map((env) => env.value);
    }

    // Multi-environment not active: use user's default environment
    return defaultEnvironment ? [defaultEnvironment] : [];
};

/**
 * Check if multi-environment mode is active
 * Returns true only if permission is enabled AND there are multiple environments available
 * If there's only one environment, multi-environment mode is disabled by design
 *
 * @param {Object} state - Redux state
 * @returns {boolean} True if multi-environment mode is active
 */
export const isMultiEnvironmentModeActive = (state) => {
    const environments = state?.serverList?.tblSucursales?.data || [];
    return state.config?.permission?.multiEnvironment && environments.length > 1;
};

/**
 * Check if multiple environments are selected
 * Returns false if:
 * - Multi-environment mode is not active (permission disabled or only 1 environment exists)
 * - Only one environment is selected
 *
 * @param {Object} state - Redux state
 * @returns {boolean} True if more than one environment is selected
 */
export const hasMultipleEnvironmentsSelected = (state) => {
    if (!isMultiEnvironmentModeActive(state)) {
        return false;
    }

    const selectedIds = getSelectedMultiEnvironmentIds(state);
    return selectedIds.length > 1;
};

// Environment field name in detail data for each entity
const DETAIL_ENVIRONMENT_FIELD = {
    companies: 'idEnvironment',
    contacts: 'idEnvironment',
    opportunities: 'idEnvironment',
    salesOrders: 'idBranch',
};

/**
 * Check if entity's environment is allowed based on selected environments
 * Used to block access to entity details when environment doesn't match
 *
 * @param {Object} state - Redux state
 * @param {Object} entity - Entity configuration object
 * @param {Object} data - Entity detail data
 * @returns {boolean} True if entity's environment is in selected environments
 */
export const isEntityEnvironmentAllowed = (state, entity, data) => {
    if (!isMultiEnvironmentModeActive(state)) return true;

    const envFieldName = DETAIL_ENVIRONMENT_FIELD[entity.entity];
    if (!envFieldName) return true;

    const selectedEnvIds = getSelectedMultiEnvironmentIds(state);
    if (!selectedEnvIds?.length) return true;

    const entityEnvValue = data?.[envFieldName];
    if (!entityEnvValue) return true;

    const entityEnvIds = String(entityEnvValue).split(/[,;]/).filter(Boolean);
    return entityEnvIds.some((envId) => selectedEnvIds.includes(envId));
};
